import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Trophy, ArrowRight, Quote, MapPin, Star as StarIcon, PlayCircle } from "lucide-react";
import api from "../../lib/api";
import useSeo from "../../hooks/useSeo";

export default function StoriesList() {
    const { i18n } = useTranslation();
    const lang = (i18n.language || "tr").split("-")[0];
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useSeo({
        title: "Başarı Hikayeleri",
        description: "Otel Pro araçları ve verileriyle başarıya ulaşan otel sahibi, GM ve yatırımcıların ilham veren hikayeleri.",
        path: "/basari-hikayeleri",
    });

    useEffect(() => {
        api.get("/stories").then(({ data }) => setItems(data.items || [])).catch(() => { }).finally(() => setLoading(false));
    }, []);

    const tr = (obj) => (obj && (obj[lang] || obj.tr)) || "";

    return (
        <div className="bg-light-bg min-h-[80vh]" data-testid="stories-list-page">
            <section className="bg-dark-navy text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#08111d] via-[#0c2438] to-[#123b52]" />
                <div className="relative max-w-5xl mx-auto px-6 py-12 lg:py-16">
                    <span className="text-xs uppercase tracking-[0.25em] text-teal font-semibold">Sektör Liderleri</span>
                    <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight inline-flex items-center gap-3">
                        <Trophy className="w-8 h-8 text-gold" /> Başarı Hikayeleri
                    </h1>
                    <p className="mt-3 text-white/75 max-w-2xl">
                        Otel sahipleri, genel müdürler ve yatırımcıların kendi sözleriyle Otel Pro deneyimleri ve sektör başarıları.
                    </p>
                </div>
            </section>

            <section className="max-w-6xl mx-auto px-6 py-12">
                {loading ? (
                    <div className="text-center text-deep-blue/60 text-sm py-12">Yükleniyor...</div>
                ) : items.length === 0 ? (
                    <div className="text-center text-deep-blue/60 text-sm py-12">Henüz başarı hikayesi yayınlanmadı.</div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="stories-list-grid">
                        {items.map((s) => (
                            <Link
                                key={s.id}
                                to={`/basari-hikayeleri/${s.seo?.slug || s.id}`}
                                data-testid={`story-card-${s.seo?.slug || s.id}`}
                                className="lift-card group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl border border-slate-100"
                            >
                                <div className="aspect-video bg-dark-navy relative overflow-hidden flex items-center justify-center">
                                    {s.person?.photo ? (
                                        <img src={s.person.photo} alt={s.person.name} className="w-full h-full object-cover" />
                                    ) : (
                                        <Trophy className="w-12 h-12 text-gold/40" />
                                    )}
                                    {s.video_url && (
                                        <span className="absolute inset-0 flex items-center justify-center bg-dark-navy/40">
                                            <PlayCircle className="w-12 h-12 text-white" />
                                        </span>
                                    )}
                                </div>
                                <div className="p-5">
                                    <Quote className="w-5 h-5 text-teal" />
                                    <p className="mt-2 text-sm text-deep-blue/85 line-clamp-3">{tr(s.featured_quote)}</p>
                                    <div className="mt-4 pt-3 border-t border-slate-100">
                                        <div className="font-semibold text-dark-navy">{s.person?.name}</div>
                                        <div className="text-xs text-deep-blue/60 inline-flex items-center gap-1.5">
                                            <span>{s.person?.title}</span>
                                        </div>
                                        <div className="mt-2 inline-flex items-center gap-3 text-xs">
                                            <span className="inline-flex items-center gap-1 text-deep-blue/70">
                                                <MapPin className="w-3 h-3" /> {s.hotel?.location}
                                            </span>
                                            <span className="inline-flex items-center gap-1 font-data text-gold-700">
                                                <StarIcon className="w-3 h-3" /> {s.hotel?.star_level}★
                                            </span>
                                        </div>
                                    </div>
                                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-teal-700 group-hover:text-gold-700">
                                        Hikayeyi Oku <ArrowRight className="w-3 h-3" />
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
